import { useState } from "react";
import { MoreHorizontal, Flag, UserX, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useToast } from "@/components/Toast";

/**
 * Feed-post moderation menu — Apple App Store 1.2 requirement for any UGC app.
 *
 * Renders as a three-dot button. Tapping opens a bottom-sheet with:
 *   • Report post   → opens ReportModal with reason picker
 *   • Block user    → confirmation → hides all of that user's posts + auto-unfollows
 *   • Cancel
 *
 * Both actions call the moderation endpoints added in server.py:
 *   POST /feed/posts/{id}/report
 *   POST /feed/block-user
 *
 * The parent component should call `onModerated()` after either action so
 * the post/list can be optimistically removed from the current view.
 */
export function PostModerationMenu({ post, currentUserId, onModerated }) {
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  // Never show the menu on your own post — you can't report/block yourself.
  if (!post?.user_id || post.user_id === currentUserId) return null;

  const handleBlock = async () => {
    if (busy) return;
    if (!window.confirm(`Block ${post.user_name || "this user"}? You won't see their posts anymore.`)) return;
    setBusy(true);
    try {
      await api.post("/feed/block-user", { blocked_user_id: post.user_id });
      toast.show(`Blocked ${post.user_name || "user"}.`, "success");
      setOpen(false);
      onModerated?.({ action: "block", userId: post.user_id });
    } catch (err) {
      toast.show(formatApiError(err), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        aria-label="Post options"
        data-testid={`post-more-btn-${post.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="icon-btn"
        style={{ padding: 6, background: "transparent", border: "none", cursor: "pointer" }}
      >
        <MoreHorizontal size={20} color="var(--text-dim)" />
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
            animation: "fadeIn 150ms ease-out",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#141414", width: "100%", maxWidth: 480,
              borderTopLeftRadius: 16, borderTopRightRadius: 16,
              padding: "12px 8px 24px", boxShadow: "0 -8px 30px rgba(0,0,0,0.5)",
              paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
            }}
          >
            <div style={{
              width: 40, height: 4, borderRadius: 2,
              background: "rgba(255,255,255,0.15)",
              margin: "4px auto 12px",
            }} />

            <SheetRow
              icon={<Flag size={18} />}
              label="Report post"
              destructive
              testId={`post-report-btn-${post.id}`}
              onClick={() => { setOpen(false); setReportOpen(true); }}
            />
            <SheetRow
              icon={<UserX size={18} />}
              label={`Block ${post.user_name || "user"}`}
              destructive
              disabled={busy}
              testId={`post-block-btn-${post.id}`}
              onClick={handleBlock}
            />
            <SheetRow
              icon={<X size={18} />}
              label="Cancel"
              testId={`post-cancel-btn-${post.id}`}
              onClick={() => setOpen(false)}
            />
          </div>
        </div>
      )}

      {reportOpen && (
        <ReportModal
          post={post}
          onClose={() => setReportOpen(false)}
          onSuccess={() => {
            setReportOpen(false);
            onModerated?.({ action: "report", postId: post.id });
          }}
        />
      )}
    </>
  );
}

function SheetRow({ icon, label, onClick, destructive, disabled, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      style={{
        width: "100%",
        display: "flex", alignItems: "center", gap: 12,
        padding: "16px 18px", borderRadius: 12,
        background: "transparent", border: "none",
        color: destructive ? "#ff5a5a" : "var(--text)",
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.6 : 1,
        fontSize: 15, fontWeight: 500,
        textAlign: "left",
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

const REPORT_REASONS = [
  { id: "spam",       label: "Spam or misleading" },
  { id: "harassment", label: "Harassment or bullying" },
  { id: "nudity",     label: "Nudity or sexual content" },
  { id: "violence",   label: "Violence or hate speech" },
  { id: "illegal",    label: "Illegal activity" },
  { id: "other",      label: "Something else" },
];

function ReportModal({ post, onClose, onSuccess }) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const submit = async () => {
    if (busy) return;
    if (!reason) {
      toast.show("Please pick a reason.", "info");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/feed/posts/${post.id}/report`, { reason, note: note.slice(0, 500) });
      toast.show("Thanks — we'll review this post.", "success");
      onSuccess();
    } catch (err) {
      toast.show(formatApiError(err), "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      data-testid="report-modal"
      style={{
        position: "fixed", inset: 0, zIndex: 110,
        background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#141414", width: "100%", maxWidth: 480,
          borderTopLeftRadius: 16, borderTopRightRadius: 16,
          padding: "16px 20px",
          paddingBottom: "calc(20px + env(safe-area-inset-bottom))",
        }}
      >
        <div style={{
          width: 40, height: 4, borderRadius: 2,
          background: "rgba(255,255,255,0.15)",
          margin: "4px auto 16px",
        }} />
        <h3 style={{ fontSize: 18, color: "var(--text)", marginBottom: 6 }}>Report post</h3>
        <p style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 16 }}>
          Reports are reviewed by our team. Repeat offenders may be removed from Nova.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {REPORT_REASONS.map((r) => (
            <button
              key={r.id}
              onClick={() => setReason(r.id)}
              data-testid={`report-reason-${r.id}`}
              style={{
                padding: "12px 14px",
                borderRadius: 10,
                border: reason === r.id ? "1px solid var(--gold)" : "1px solid rgba(255,255,255,0.08)",
                background: reason === r.id ? "rgba(178,138,62,0.08)" : "transparent",
                color: "var(--text)",
                textAlign: "left", fontSize: 14,
                cursor: "pointer",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        <textarea
          className="input"
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything else you'd like to tell us? (optional)"
          data-testid="report-note-input"
          style={{ width: "100%", resize: "vertical", marginBottom: 16 }}
        />

        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onClose}
            className="btn btn-ghost"
            style={{ flex: 1 }}
            data-testid="report-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="report-submit-btn"
            className="btn btn-primary"
            style={{
              flex: 1,
              background: "#ff5a5a",
              color: "#0a0a0a",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "Reporting…" : "Report"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PostModerationMenu;
